import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { SelectV2, SelectV2Props } from "PFComponents/select_v2";
import { FilterWithDefaults, Id } from "PFTypes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { areFilterValuesEqual, letFilterRestore } from "../../use_filtered_collection";
import { createDropdownId } from "./filters.utils";
import { useDropdownWidth } from "./use_dropdown_width";
import { OptionOriginal, useFiltersOptions } from "./use_filters_options";

type SelectManyItemProps = SelectV2Props<OptionOriginal> & {
  filter: FilterWithDefaults;
  onChange: (ids: Id[] | null, values?: OptionOriginal[] | null) => void;
  searchId?: number;
  disabled?: boolean;
};

export const SelectManyItem = ({ filter, onChange, disabled, labelIcons, searchId }: SelectManyItemProps) => {
  const { t } = useTranslation("filters");

  const { optionsProps } = useFiltersOptions({ filter });

  const { dropdownWidth, ref } = useDropdownWidth();

  const letRestore = letFilterRestore(filter);

  const value = useMemo(() => {
    if (isNil(filter.value)) {
      return [];
    }

    return ((isArray(filter.value) ? filter.value : [filter.value]) || []) as Id[];
  }, [filter.value]);

  const [tempMultiValues, setTempMultiValues] = useState<Id[]>(value);
  const [tempMultiObjects, setTempMultiObjects] = useState<OptionOriginal[]>([]);

  useEffect(() => setTempMultiValues(value), [value]);

  const renderDisplayValue = (value) => {
    if (!value || value.length === 0) {
      return "";
    }
    const isOR = filter.operator === "any";
    const label = isOR
      ? t("preview.filtersSelectedOR", { count: value.length })
      : t("preview.filtersSelected", { count: value.length });
    return label;
  };

  const handleChange = (value) => {
    setTempMultiValues(value.map(({ id }) => id));
    setTempMultiObjects(value.map(({ original }) => original));
  };
  const handleClear = () => onChange(null);
  const handleDropdownClose = () =>
    !areFilterValuesEqual(value, tempMultiValues) && onChange(tempMultiValues, tempMultiObjects);

  const noValue = !value || isEmpty(value);

  return (
    <SelectV2<OptionOriginal>
      ref={ref}
      disabled={filter.disabled || disabled}
      label={filter.title}
      labelIcons={labelIcons}
      required={filter.required}
      value={tempMultiValues}
      onChange={handleChange}
      onRestore={
        letRestore ? () => onChange((filter.defaultValue ?? filter.default_value) as Id[]) : undefined
      }
      onClear={noValue || filter.required ? undefined : handleClear}
      onDropdownClose={handleDropdownClose}
      renderDisplayValue={renderDisplayValue}
      dropdownId={createDropdownId(filter)}
      // search id is forwarder here to trigger options refetch on search update
      // however segment filters always get the same set of all existing options
      searchId={filter.segment ? undefined : searchId}
      dropdownWidth={dropdownWidth}
      multiple
      clipOptionsOverflow={false}
      {...optionsProps}
    />
  );
};
